import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  LeadStatus,
  StudioLead,
  StudioLeadInput,
  TrialBooking,
  TrialBookingInput,
} from '@pirouette/shared';
import {
  leadStatusSchema,
  studioLeadInputSchema,
  trialBookingInputSchema,
} from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { stripUndefined } from '@/lib/db';

export type StudioLeadListItem = StudioLead & {
  program_offerings?: { id: string; name: string } | null;
};

export type StudioLeadDetails = StudioLeadListItem & {
  trial_bookings?: TrialBooking[];
};

export function useLeads(status?: LeadStatus) {
  return useQuery({
    queryKey: ['leads', status ?? 'all'],
    queryFn: async (): Promise<StudioLeadListItem[]> => {
      let q = supabase
        .from('studio_leads')
        .select('*, program_offerings(id, name)')
        .order('created_at', { ascending: false });
      if (status) q = q.eq('lead_status', status);
      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
  });
}

export function useLead(id: string | undefined) {
  return useQuery({
    enabled: !!id,
    queryKey: ['leads', id],
    queryFn: async (): Promise<StudioLeadDetails | null> => {
      const { data, error } = await supabase
        .from('studio_leads')
        .select('*, program_offerings(id, name), trial_bookings(*)')
        .eq('id', id!)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });
}

export function useCreateLead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: StudioLeadInput): Promise<StudioLead> => {
      const parsed = studioLeadInputSchema.parse(input);
      const { data, error } = await supabase
        .from('studio_leads')
        .insert(stripUndefined(parsed))
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });
}

export function useUpdateLeadStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: LeadStatus }): Promise<StudioLead> => {
      const parsedStatus = leadStatusSchema.parse(status);
      const { data, error } = await supabase
        .from('studio_leads')
        .update({ lead_status: parsedStatus })
        .eq('id', id)
        .select('*')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (lead) => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['leads', lead.id] });
    },
  });
}

export function useCreateTrialBooking() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: TrialBookingInput): Promise<TrialBooking> => {
      const parsed = trialBookingInputSchema.parse(input);
      const { data, error } = await supabase
        .from('trial_bookings')
        .insert(stripUndefined(parsed))
        .select('*')
        .single();
      if (error) throw error;
      const booking = data;
      return booking;
    },
    onSuccess: (booking) => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['leads', booking.lead_id] });
    },
  });
}
